import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  query,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MigrationReports = () => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [migrationHistory, setMigrationHistory] = useState([]);
  const [summary, setSummary] = useState({
    totalMigrations: 0,
    successfulMigrations: 0,
    failedMigrations: 0,
    lastMigration: null
  });

  useEffect(() => {
    if (userData?.establishmentId) {
      fetchMigrationHistory();
    }
  }, [userData?.establishmentId]);

  const fetchMigrationHistory = async () => {
    setLoading(true);
    try {
      // Récupérer l'historique des archives (migrations)
      const archivesRef = collection(db, 'establishments', userData.establishmentId, 'archives');
      const archivesQuery = query(archivesRef, orderBy('createdAt', 'desc'), limit(20));
      const archivesSnapshot = await getDocs(archivesQuery);
      
      const migrations = archivesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          year: doc.id,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          status: data.status || 'completed',
          migratedBy: data.migratedBy,
          absencesCount: data.absencesCount || 0,
          messagesCount: data.messagesCount || 0,
          errors: data.errors || [],
          duration: data.duration || 'N/A'
        };
      });

      setMigrationHistory(migrations);
      
      // Calculer le résumé
      const successful = migrations.filter(m => m.status === 'completed').length;
      const failed = migrations.filter(m => m.status === 'failed').length;
      
      setSummary({
        totalMigrations: migrations.length,
        successfulMigrations: successful,
        failedMigrations: failed,
        lastMigration: migrations.length > 0 ? migrations[0] : null
      });

    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'in_progress':
        return <WarningIcon color="warning" />;
      default:
        return <CheckCircleIcon color="success" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'warning';
      default: return 'default';
    }
  };

  const exportReport = () => {
    const reportData = {
      establishmentId: userData.establishmentId,
      generatedAt: new Date().toISOString(),
      summary,
      migrationHistory,
      metadata: {
        totalArchives: migrationHistory.length,
        oldestArchive: migrationHistory.length > 0 ? migrationHistory[migrationHistory.length - 1].year : null,
        newestArchive: migrationHistory.length > 0 ? migrationHistory[0].year : null
      }
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_migrations_${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssessmentIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h5">Rapports de Migration</Typography>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportReport}
            disabled={migrationHistory.length === 0}
          >
            Exporter le Rapport
          </Button>
        </Box>
      </Box>

      {/* Résumé */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {summary.totalMigrations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Migrations totales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {summary.successfulMigrations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Réussies
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error.main">
                {summary.failedMigrations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Échouées
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.primary">
                {summary.lastMigration ? summary.lastMigration.year : 'Aucune'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dernière migration
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dernière migration */}
      {summary.lastMigration && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={getStatusIcon(summary.lastMigration.status)}
        >
          <Typography variant="body1">
            <strong>Dernière migration:</strong> {summary.lastMigration.year} - 
            {format(summary.lastMigration.createdAt, ' dd MMMM yyyy à HH:mm', { locale: fr })}
          </Typography>
          <Typography variant="body2">
            {summary.lastMigration.absencesCount} absences et {summary.lastMigration.messagesCount} messages archivés
          </Typography>
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Historique des migrations */}
      <Typography variant="h6" gutterBottom>
        Historique des Migrations
      </Typography>
      
      {migrationHistory.length === 0 ? (
        <Alert severity="info">
          Aucune migration n'a encore été effectuée.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Année Scolaire</TableCell>
                <TableCell>Date de Migration</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Données Migrées</TableCell>
                <TableCell>Durée</TableCell>
                <TableCell>Migré par</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {migrationHistory.map((migration) => (
                <TableRow key={migration.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {migration.year}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {format(migration.createdAt, 'dd/MM/yyyy HH:mm', { locale: fr })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(migration.status)}
                      label={migration.status === 'completed' ? 'Réussie' : 
                             migration.status === 'failed' ? 'Échouée' : 'En cours'}
                      color={getStatusColor(migration.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={`${migration.absencesCount} absences`} 
                        size="small" 
                        variant="outlined"
                      />
                      <Chip 
                        label={`${migration.messagesCount} messages`} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{migration.duration}</TableCell>
                  <TableCell>{migration.migratedBy || 'Système'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MigrationReports;
